import { loginSchema } from '@ifretes/shared'
import { useState } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { Botao, Campo, Cartao, ErroApi, Mensagem } from '../../components/ui'
import { ApiError } from '../../lib/api'
import { useTheme } from '../../theme/ThemeProvider'
import { useAuth } from './store'

export function Entrar() {
  const { entrar } = useAuth()
  const navegar = useNavigate()
  const local = useLocation()

  /**
   * Para onde ir depois de entrar.
   *
   * O guardiao de rota grava o destino em `state.de` quando intercepta alguem
   * deslogado — mas nada lia esse valor, e todo login terminava no painel. Quem
   * clicava num link de frete compartilhado, ou usava a busca da vitrine,
   * perdia a intencao no caminho.
   *
   * So caminhos internos: aceitar um destino arbitrario transformaria o login
   * num redirecionador aberto, util para phishing ("entre no iFretes" levando
   * a outro dominio depois da senha).
   */
  const destino = (() => {
    const de = (local.state as { de?: string } | null)?.de
    if (typeof de !== 'string') return '/painel'
    if (!de.startsWith('/') || de.startsWith('//')) return '/painel'
    // Voltar para as telas de autenticacao seria um laco.
    if (/^\/(entrar|criar-conta|esqueci-senha|redefinir-senha|mfa-obrigatorio)/.test(de)) {
      return '/painel'
    }
    return de
  })()
  const [params] = useSearchParams()
  const { theme, resolved, toggle } = useTheme()

  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [totp, setTotp] = useState('')
  const [usandoRecuperacao, setUsandoRecuperacao] = useState(false)
  const [pedeTotp, setPedeTotp] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<unknown>(null)
  const [campos, setCampos] = useState<Record<string, string[]>>({})

  const emailConfirmado = params.get('confirmado') === '1'
  const senhaRedefinida = params.get('senha') === 'ok'

  async function enviar(e: React.FormEvent) {
    e.preventDefault()
    setErro(null)
    setCampos({})

    // Valida no cliente com o MESMO schema da API: erro de digitação não
    // precisa de ida e volta ao servidor.
    const r = loginSchema.safeParse({ email, password: senha, ...(totp ? { totp } : {}) })
    if (!r.success) {
      const mapa: Record<string, string[]> = {}
      for (const i of r.error.issues) {
        const k = i.path.join('.') || '_'
        ;(mapa[k] ??= []).push(i.message)
      }
      setCampos(mapa)
      return
    }

    setEnviando(true)
    try {
      await entrar(r.data.email, r.data.password, r.data.totp)
      navegar(destino, { replace: true })
    } catch (err) {
      if (err instanceof ApiError) {
        // A senha conferiu e falta o segundo fator: em vez de erro, o
        // formulário ganha o campo do código.
        if (err.precisaTotp) {
          setPedeTotp(true)
          setEnviando(false)
          return
        }
        // Papel privilegiado sem MFA cadastrado: leva ao cadastro do 2º fator.
        if (err.mfaEnrollmentRequired) {
          navegar('/mfa-obrigatorio', { state: { email: r.data.email } })
          return
        }
        setCampos(err.camposComErro)
      }
      setErro(err)
      setEnviando(false)
    }
  }

  return (
    <div className="pagina-centrada">
      <div className="ifr-coluna-centrada">
        <div className="ifr-linha ifr-linha--fim ifr-mb">
          <button
            className="br-button circle small"
            onClick={toggle}
            aria-label={`Tema atual: ${
              theme === 'system' ? `sistema (${resolved})` : theme
            }. Alternar tema.`}
          >
            <i
              className={`fas ${
                theme === 'light' ? 'fa-sun' : theme === 'dark' ? 'fa-moon' : 'fa-adjust'
              }`}
              aria-hidden="true"
            />
          </button>
        </div>

        <Cartao titulo="Entrar no iFretes" descricao="Fretes planejados, escolhidos por você.">
          {emailConfirmado && (
            <div className="ifr-mb">
              <Mensagem tipo="success" titulo="E-mail confirmado">
                Sua conta está ativa. Entre com seus dados.
              </Mensagem>
            </div>
          )}
          {senhaRedefinida && (
            <div className="ifr-mb">
              <Mensagem tipo="success" titulo="Senha redefinida">
                Entre com a nova senha.
              </Mensagem>
            </div>
          )}
          <ErroApi erro={erro} />

          <form onSubmit={enviar} noValidate className="ifr-pilha">
            <Campo
              id="email"
              rotulo="E-mail"
              tipo="email"
              valor={email}
              aoMudar={setEmail}
              erros={campos.email}
              obrigatorio
              autoComplete="username"
              inputMode="email"
              autoFocus
            />

            <Campo
              id="senha"
              rotulo="Senha"
              tipo="password"
              valor={senha}
              aoMudar={setSenha}
              erros={campos.password}
              obrigatorio
              autoComplete="current-password"
            />

            {pedeTotp && (
              <>
                {/* Duas formas de segundo fator, com campos DIFERENTES.
                    Antes havia um só, e ele filtrava assim:
                      `v.replace(/\D/g, '').slice(0, 6)`  + maxLength={6}
                    Isso apagava tudo o que não fosse dígito, então um código de
                    recuperação (`80FF-3E47-B9B8-C443`) era impossível de
                    digitar — sobrava "803" no campo. Os dez códigos ficavam
                    inalcançáveis pela tela mesmo depois de a API passar a
                    aceitá-los.
                    E `inputMode="numeric"` no iPhone abre um teclado SEM
                    letras: não bastaria remover o filtro. */}
                <Mensagem tipo="info" titulo="Verificação em dois fatores">
                  {usandoRecuperacao
                    ? 'Informe um dos códigos de recuperação que você guardou. Cada um serve uma única vez.'
                    : 'Informe o código de 6 dígitos do seu aplicativo autenticador.'}
                </Mensagem>

                {usandoRecuperacao ? (
                  <Campo
                    id="totp"
                    rotulo="Código de recuperação"
                    valor={totp}
                    // Aceita letra, dígito e hífen. O servidor normaliza caixa
                    // e separadores, então não é preciso apertar aqui.
                    aoMudar={(v) => setTotp(v.replace(/[^a-zA-Z0-9-]/g, '').slice(0, 24))}
                    erros={campos.totp}
                    obrigatorio
                    inputMode="text"
                    maxLength={24}
                    placeholder="XXXX-XXXX-XXXX-XXXX"
                    autoFocus
                  />
                ) : (
                  <Campo
                    id="totp"
                    rotulo="Código de verificação"
                    valor={totp}
                    aoMudar={(v) => setTotp(v.replace(/\D/g, '').slice(0, 6))}
                    erros={campos.totp}
                    obrigatorio
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="000000"
                    autoFocus
                  />
                )}

                <button
                  type="button"
                  className="ifr-link-botao"
                  onClick={() => {
                    // Limpa ao trocar: o valor digitado numa forma nunca serve
                    // na outra, e deixá-lo lá só produziria um erro confuso.
                    setTotp('')
                    setUsandoRecuperacao((v) => !v)
                  }}
                >
                  {usandoRecuperacao
                    ? 'Voltar ao código do aplicativo'
                    : 'Perdi o acesso ao aplicativo — usar código de recuperação'}
                </button>
              </>
            )}

            <Botao tipo="submit" carregando={enviando} larguraTotal>
              Entrar
            </Botao>
          </form>

          <div
            className="ifr-divisor ifr-mt-g ifr-pilha ifr-pilha--curta"
          >
            <Link to="/esqueci-senha" className="ifr-marca-cor">
              Esqueci minha senha
            </Link>
            <span className="ifr-fraco">
              Não tem conta?{' '}
              <Link to="/criar-conta" className="ifr-elo">
                Criar conta
              </Link>
            </span>
          </div>
        </Cartao>
      </div>
    </div>
  )
}
